import { Input, Select, useNotification } from '@aigc/antd';
import { useAccept, useAutoPaste } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { tryURL } from '@aigc/toolkit';
import {
  ArrowUpOutlined,
  CloudUploadOutlined,
  PaperClipOutlined,
  PictureOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Attachments, Sender } from '@ant-design/x';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import {
  Button,
  Dropdown,
  Form,
  Popconfirm,
  Space,
  Tooltip,
  type GetRef,
} from 'antd';
import { useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { array, object, string } from 'yup';
import { FormControl } from '../../components/form-control';
import { UploadedImageListModal } from '../../components/uploaded-image-list-modal';
import { languages } from '../common/model';
import { createBeforeUpload, FileValidators } from '../file';
import type { FileDto } from '../file/model';
import { fileSchema } from '../file/schema';
import { TaskType } from '../task/model';
import { translateImageMutationOptions } from './api';
import { TranslateImageList } from './components/translate-image-list';
import {
  imageTranslationModelSpecs,
  type ImageTranslationModelCode,
} from './image-llm';

const DEFAULT_TRANSLATION_MODEL: ImageTranslationModelCode =
  'gemini-3.1-flash-image';

/** 最大文件大小 20MB */
const MAX_FILE_SIZE = 20 * 1024 * 1024;

/** 宽高比范围 */
const MIN_ASPECT_RATIO = 1 / 16;
const MAX_ASPECT_RATIO = 16;

/** 宽高像素范围 */
const MIN_DIMENSION = 14;
const MAX_DIMENSION = 4096;

const imageTranslationSchema = object({
  model: string<ImageTranslationModelCode>().required('请选择模型'),
  name: string().nullable(),
  languages: array().of(string().required()).min(1).nullable().required(),
  file_list: fileSchema
    .label('图片')
    .meta({ select: true })
    .max(9)
    .min(1)
    .required(),
  instructions: string().label('其他要求').nullable(),
});

export const ImageTranslationPage = () => {
  const notification = useNotification();
  const sender_ref = useRef<GetRef<typeof Sender>>(null);
  const attachments_ref = useRef<GetRef<typeof Attachments>>(null);
  const form = useForm({
    defaultValues: { model: DEFAULT_TRANSLATION_MODEL },
    resolver: yupResolver(imageTranslationSchema),
  });
  const { control, setValue, getValues } = form;
  const [openOverlay] = useOverlay<FileDto>();
  const translate = useMutation(translateImageMutationOptions);

  const accept = useAccept(['.png', '.jpeg', '.jpg', '.bmp']);
  useAutoPaste('#uploader', { filter: accept.filter });

  const sender_header = (
    <Sender.Header
      open
      title="待翻译的图片"
      closable={false}
      classNames={{ content: '!py-0' }}
    >
      <FormControl
        control={control}
        name="file_list"
        render={({ field }) => (
          <Attachments
            id="uploader"
            ref={attachments_ref}
            name={field.name}
            classNames={{ list: '-mx-4' }}
            disabled={field.disabled}
            multiple
            items={field.value}
            maxCount={8}
            accept={accept}
            beforeUpload={createBeforeUpload({
              validator: FileValidators.compose([
                FileValidators.accept(accept.format),
                FileValidators.maxSize(MAX_FILE_SIZE, { exclusive: true }),
                FileValidators.dimensions({
                  min: MIN_DIMENSION,
                  max: MAX_DIMENSION,
                }),
                FileValidators.aspectRatio({
                  min: MIN_ASPECT_RATIO,
                  max: MAX_ASPECT_RATIO,
                  exclusive: true,
                }),
              ]),
              onError: ({ title, message }) => {
                notification.error({ title, description: message });
              },
            })}
            onChange={(info) => {
              if (info.file.status === 'error') {
                notification.error({
                  title: '错误',
                  description: `图片 ${info.file.name} 上传失败，请重试`,
                });
              }

              field.onChange(info.fileList);
            }}
            placeholder={(type) =>
              type === 'drop'
                ? { title: '文件放这里' }
                : {
                    icon: <CloudUploadOutlined />,
                    title: '上传文件',
                    description: (
                      <>
                        点击、拖放或<span className="text-blue-500">粘贴</span>
                        文件到此区域进行上传
                      </>
                    ),
                  }
            }
            getDropContainer={() => sender_ref.current?.nativeElement}
          />
        )}
      />
    </Sender.Header>
  );

  const onSubmit = form.handleSubmit(async (values) => {
    await translate.mutateAsync({
      model: values.model,
      name: values.name ?? void 0,
      target_languages: values.languages,
      images: values.file_list.map((file) => ({
        name: file.name,
        type: file.type!,
        url: file.response!.file_url,
      })),
      instructions: values.instructions ?? void 0,
    });
  });

  return (
    <div className="flex w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:h-full sm:flex-row sm:divide-x sm:divide-y-0">
      <div className="card animate-fade-in sm:overflow-auto xl:w-[640px]">
        <Form layout="vertical">
          <FormControl
            control={control}
            name="name"
            label="名称"
            render={({ field }) => (
              <Input {...field} value={field.value ?? ''} />
            )}
          />
          <FormControl
            control={control}
            name="languages"
            label="目标语言"
            required
            render={({ field }) => (
              <Select
                {...field}
                mode="tags"
                placeholder="请选择/输入目标语言"
                options={languages.map((lang) => ({
                  label: lang,
                  value: lang,
                }))}
              ></Select>
            )}
          />
          <FormControl
            control={control}
            name="instructions"
            render={({ field }) => (
              <Sender
                ref={sender_ref}
                header={sender_header}
                footer={(_, { components }) => {
                  const { LoadingButton } = components;

                  return (
                    <div className="flex items-center">
                      <Dropdown
                        trigger={['click']}
                        placement="topLeft"
                        arrow={{ pointAtCenter: true }}
                        menu={{
                          items: [
                            {
                              key: 'image',
                              label: (
                                <div className="flex items-center gap-1">
                                  <UploadOutlined />
                                  <span>上传</span>
                                </div>
                              ),
                              onClick: () => {
                                attachments_ref.current?.select({
                                  multiple: true,
                                });
                              },
                            },
                            {
                              key: 'library',
                              label: (
                                <div className="flex items-center gap-1">
                                  <PictureOutlined />
                                  <span>我的图片库</span>
                                </div>
                              ),
                              onClick: async () => {
                                const overlay_ref = openOverlay(
                                  <UploadedImageListModal />,
                                );
                                const file = await overlay_ref.afterClosed;

                                if (!file) {
                                  return;
                                }

                                const url = tryURL(
                                  file.oss_object_key,
                                  file.oss_host,
                                );

                                if (!url) {
                                  return;
                                }

                                setValue('file_list', [
                                  ...getValues('file_list'),
                                  {
                                    uid: uuid(),
                                    name: file.filename,
                                    url: url.toString(),
                                    thumbUrl: url.toString(),
                                    status: 'done',
                                    response: {
                                      id: file.id,
                                      filename: file.filename,
                                      mime: file.mime,
                                      file_url: url.toString(),
                                      created_at: file.created_at,
                                    },
                                  },
                                ]);
                              },
                            },
                          ],
                        }}
                      >
                        <Button
                          variant="text"
                          color="primary"
                          classNames={{ icon: 'text-base' }}
                          icon={<PaperClipOutlined />}
                        />
                      </Dropdown>
                      <Controller
                        control={control}
                        name="model"
                        render={({ field: modelField }) => (
                          <Select
                            {...modelField}
                            aria-label="图片翻译模型"
                            classNames={{ root: '!w-36' }}
                            allowClear={false}
                            disabled={
                              modelField.disabled || translate.isPending
                            }
                            options={Array.from(
                              imageTranslationModelSpecs,
                              ([value, spec]) => ({
                                label: spec.name,
                                value,
                              }),
                            )}
                          />
                        )}
                      />
                      <div className="flex-auto" />
                      <Space>
                        {translate.isPending ? (
                          <LoadingButton type="default" />
                        ) : (
                          <Popconfirm
                            title="确定提交翻译任务？"
                            onConfirm={onSubmit}
                          >
                            <Button
                              shape="round"
                              icon={<ArrowUpOutlined />}
                              type="primary"
                            />
                          </Popconfirm>
                        )}
                        <Tooltip title="重置表单">
                          <Button
                            variant="filled"
                            color="primary"
                            shape="circle"
                            classNames={{ icon: 'text-base' }}
                            icon={<ReloadOutlined />}
                            aria-label="重置"
                            disabled={translate.isPending}
                            onClick={() => {
                              form.reset({
                                model: DEFAULT_TRANSLATION_MODEL,
                                name: '',
                                languages: [],
                                file_list: [],
                                instructions: '',
                              });
                            }}
                          />
                        </Tooltip>
                      </Space>
                    </div>
                  );
                }}
                value={field.value ?? ''}
                placeholder="可以输入其他要求"
                autoSize={{ minRows: 3 }}
                disabled={field.disabled}
                onBlur={field.onBlur}
                onChange={field.onChange}
                suffix={false}
              />
            )}
          />
        </Form>
      </div>

      <div className="card flex-auto animate-slide-up space-y-6 sm:min-h-40 sm:flex-1 sm:overflow-auto">
        <TranslateImageList
          onEdit={(task) => {
            switch (task.type) {
              case TaskType.批量图片翻译:
                form.setValues((values) => ({
                  ...values,
                  languages: task.generate_data.target_languages,
                  file_list: task.generate_data.images.map((image) => ({
                    uid: uuid(),
                    name: image.name,
                    type: image.type,
                    status: 'done',
                    url: image.url,
                    response: {
                      filename: image.name,
                      mime: image.type,
                      file_url: image.url,
                    },
                  })),
                }));
                break;
              default:
                form.setValues((values) => ({
                  ...values,
                  languages: [task.generate_data.target_language],
                  file_list: [
                    {
                      uid: uuid(),
                      name: task.generate_data.image.name,
                      type: task.generate_data.image.type,
                      status: 'done',
                      url: task.generate_data.image.url,
                      response: {
                        filename: task.generate_data.image.name,
                        mime: task.generate_data.image.type,
                        file_url: task.generate_data.image.url,
                      },
                    },
                  ],
                }));
            }
          }}
        />
      </div>
    </div>
  );
};
